import React, { useState, useEffect } from 'react';
import { showToast } from 'stores/ToastStore';
import { titleize } from 'underscore.string';
import { pick } from 'lodash';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import SessionStore from '../../../../stores/SessionStore';
import { getShipper, updateShipperFscConfig, } from '../../../../utils/CustomersAPIUtils';
import CargomaticCard from '../../../v2/card';
import EditButton from '../../../v2/buttons/EditButton';
import Alert from '../../../ui/Alert';
import CargomaticSelect from '../../../v2/select';
import CargomaticCheckbox from '../../../v2/forms/Checkbox';
import DescriptionListItem from '../../../v2/DescriptionListItem';
import seriesIds from '../../../../constants/RateSeriesIds';
import LoadingCircle from '../../../ui/LoadingCircle';
import { fuelWeeks, fuelDays, fuelMatrixOptions, } from '../../../../constants/fsc';
const defaultFsc = {
    series_id: '',
    fuelWeek: '',
    fuelDay: '',
    fuelMatrix: '',
};
function FscSettings({ shipperId, t }) {
    const [editing, setEditing] = useState(false);
    const [fscConfiguration, setFscConfiguration] = useState(null);
    const [applied, setApplied] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [loaded, setLoaded] = useState(false);
    function handleSessionStoreChange() {
        setCanEdit(SessionStore.isAbleToEditShorthaulFSC());
    }
    useEffect(() => {
        async function load() {
            const res = await getShipper(shipperId);
            const data = res.entity;
            setFscConfiguration(data.fsc_config || defaultFsc);
            setApplied(data.use_fsc);
            setLoaded(true);
        }
        load();
        setCanEdit(SessionStore.isAbleToEditShorthaulFSC());
        SessionStore.addChangeListener(handleSessionStoreChange);
        return () => {
            SessionStore.removeChangeListener(handleSessionStoreChange);
        };
    }, []);
    function handleEdit() {
        setEditing(true);
    }
    function handleCancel() {
        setEditing(false);
    }
    async function handleSave(updates) {
        let finalUpdates;
        if (updates.hasOwnProperty('use_fsc') && !updates.use_fsc) {
            finalUpdates = { use_fsc: false };
        }
        else {
            finalUpdates = updates;
        }
        try {
            const { entity: { data }, } = await updateShipperFscConfig(shipperId, finalUpdates);
            showToast(t('UPDATE_FSC_CONFIG'), t('SUCCESSFULLY_UPDATED_FSC'));
            setFscConfiguration(data.fsc_config);
            setApplied(data.use_fsc);
        }
        catch (e) {
            console.log(e);
            showToast(t('UPDATE_FSC_CONFIG'), t('FAILURE_FSC_UPDATE'), 'error');
        }
        finally {
            setEditing(false);
        }
    }
    function renderHeader(headerParams) {
        const { canEdit } = headerParams;
        return React.createElement(Header, { canEdit: canEdit, onEdit: handleEdit, t: t });
    }
    return (React.createElement(CargomaticCard, { header: renderHeader, headerParams: { canEdit: SessionStore.isAbleToEditShorthaulFSC() } },
        !loaded && React.createElement(LoadingCircle, null),
        loaded && !editing && (React.createElement(FscSettingsSummary, { fscApplied: applied, fscConfiguration: fscConfiguration, t: t })),
        canEdit && loaded && editing && (React.createElement(FscSettingsForm, { fscConfiguration: fscConfiguration, fscApplied: applied, onSave: handleSave, onCancel: handleCancel, t: t }))));
}
function FscSettingsForm({ fscApplied, fscConfiguration, onSave, onCancel, t, }) {
    const [errors, setErrors] = useState({});
    const [applied, setApplied] = useState(false);
    const [updates, setUpdates] = useState(null);
    const series_id = (updates === null || updates === void 0 ? void 0 : updates.series_id) || '';
    const fuelWeek = (updates === null || updates === void 0 ? void 0 : updates.fuelWeek) || '';
    const fuelDay = (updates === null || updates === void 0 ? void 0 : updates.fuelDay) || '';
    const fuelMatrix = (updates === null || updates === void 0 ? void 0 : updates.fuelMatrix) || '';
    useEffect(() => {
        setApplied(fscApplied);
    }, [fscApplied]);
    useEffect(() => {
        const mappedUpdates = pick(fscConfiguration || {}, [
            'series_id',
            'fuelWeek',
            'fuelDay',
            'fuelMatrix',
        ]);
        setUpdates(mappedUpdates);
    }, [fscConfiguration]);
    function handleSave() {
        const formErrors = {};
        if (!applied) {
            setErrors(formErrors);
            onSave({ use_fsc: applied, fsc_config: null });
            return;
        }
        if (series_id || fuelWeek || fuelDay || fuelMatrix) {
            if (!series_id) {
                formErrors.series_id = t('SELECT_SERIES_ID_TIP');
            }
            if (!fuelWeek) {
                formErrors.fuelWeek = t('SELECT_FUEL_WEEK_TIP');
            }
            if (!fuelDay) {
                formErrors.fuelDay = t('SELECT_FUEL_DAY_TIP');
            }
            if (!fuelMatrix) {
                formErrors.fuelMatrix = t('SELECT_FUEL_MATRIX_TIP');
            }
        }
        setErrors(formErrors);
        if (Object.keys(formErrors).length === 0) {
            const isConfigEmpty = Object.values(updates || {}).every((value) => !value);
            onSave({ use_fsc: applied, fsc_config: isConfigEmpty ? null : updates });
        }
    }
    function handleFieldChange(field) {
        return function ({ value }) {
            setUpdates(Object.assign(Object.assign({}, updates), { [field]: value || '' }));
        };
    }
    function handleToggleApplied(checked) {
        setApplied(checked);
    }
    function renderErrors() {
        if (Object.keys(errors).length === 0) {
            return null;
        }
        return (React.createElement("div", { className: "mb-4" },
            React.createElement(Alert, { type: "error", title: titleize(t('ERROR', { count: 2 })) },
                React.createElement("ul", null, Object.values(errors).map((error, i) => (React.createElement("li", { key: i }, error)))))));
    }
    return (React.createElement("div", { className: "" },
        renderErrors(),
        React.createElement("div", { className: "mb-4" },
            React.createElement(CargomaticCheckbox, { id: "sharedFilter", checked: applied, onChange: handleToggleApplied }, t('APPLIED'))),
        applied && (React.createElement("div", null,
            React.createElement(CargomaticSelect, { className: "mb-4", valueKey: "value", textKey: "name", label: t('SERIES_ID'), items: seriesIds, selected: series_id, showCaption: true, caption: t('SELECT_SERIES_ID'), onSelect: handleFieldChange('series_id') }),
            React.createElement(CargomaticSelect, { className: "mb-4", valueKey: "value", textKey: "localizationSlug", label: t('FUEL_WEEK'), items: fuelWeeks, selected: fuelWeek, showCaption: true, caption: t('SELECT_FUEL_WEEK'), onSelect: handleFieldChange('fuelWeek') }),
            React.createElement(CargomaticSelect, { className: "mb-4", valueKey: "value", textKey: "localizationSlug", label: t('FUEL_DAY'), items: fuelDays, selected: fuelDay, showCaption: true, caption: t('SELECT_DAY'), onSelect: handleFieldChange('fuelDay') }),
            React.createElement(CargomaticSelect, { className: "mb-4", valueKey: "value", textKey: "localizationSlug", label: t('FUEL_MATRIX'), items: fuelMatrixOptions, selected: fuelMatrix, showCaption: true, caption: t('SELECT_FUEL_MATRIX'), onSelect: handleFieldChange('fuelMatrix') }))),
        React.createElement("div", { className: "flex justify-end items-center space-x-2" },
            React.createElement("button", { type: "button", className: "button button-green", onClick: handleSave }, titleize(t('SAVE'))),
            React.createElement("button", { type: "button", className: "button button-secondary", onClick: onCancel }, t('CANCEL')))));
}
function FscSettingsSummary({ fscApplied, fscConfiguration, t }) {
    var _a;
    return (React.createElement("div", null,
        !fscApplied && React.createElement(Alert, { type: "warning" }, t('FSC_NOT_APPLIED')),
        fscApplied && (React.createElement("div", { className: "sm:grid sm:grid-cols-2 sm:gap-3" },
            React.createElement(DescriptionListItem, { label: t('SERIES_ID'), className: "mb-4" }, ((_a = seriesIds.find((config) => config.value === (fscConfiguration === null || fscConfiguration === void 0 ? void 0 : fscConfiguration.series_id))) === null || _a === void 0 ? void 0 : _a.name) || t('NOT_SET')),
            React.createElement(DescriptionListItem, { label: t('FUEL_WEEK'), className: "mb-4" }, titleize(t(sluggify(fscConfiguration === null || fscConfiguration === void 0 ? void 0 : fscConfiguration.fuelWeek))) || t('NOT_SET')),
            React.createElement(DescriptionListItem, { label: t('FUEL_DAY'), className: "mb-4" }, titleize(t(sluggify(fscConfiguration === null || fscConfiguration === void 0 ? void 0 : fscConfiguration.fuelDay))) || t('NOT_SET')),
            React.createElement(DescriptionListItem, { label: t('FUEL_MATRIX'), className: "mb-4" }, titleize(t(sluggify(fscConfiguration === null || fscConfiguration === void 0 ? void 0 : fscConfiguration.fuelMatrix))) ||
                t('NOT_SET'))))));
}
function Header({ canEdit, onEdit, t }) {
    return (React.createElement("div", { className: "flex justify-between items-center" },
        React.createElement("h3", { className: "my-0 flex-1 mr-4" }, t('SHORTHAUL_FUEL_SURCHARGE')),
        React.createElement(EditButton, { size: "small", onClick: onEdit, disabled: !canEdit })));
}
export default withTranslation()(FscSettings);
